import { FunctionComponent, useEffect, useState } from 'react';
import GenericModal from '../../../../ui/GenericModal';
import { FormProvider, useForm } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import LoadingButton from '../../../../ui/LoadingButton';
import TokenService from '../../../../../services/token-service';
import {
  useCreateKidDocumentMutation,
  useCreateKidCosts,
  useCalculateKidFigures,
} from '../../../../../api/kids/hooks/mutations';
import {
  useGetKidCostVariables,
  useGetLatestKidFigures,
} from '../../../../../api/kids/hooks/queries';
import CreateKidDocumentStepper from './form/CreateKidDocumentStepper';
import StepCostsAndFigures from './form/StepCostsAndFigures';
import StepReview from './form/StepReview';
import StepDocumentType from './form/StepDocumentType';
import { formSliderVariants } from '../../../../ui/animations/layout.animations';
import { getUploadErrorMessage } from '../../../../../api/upload/upload.routes';
import {
  toBackendCost,
  toDisplayCost,
  getKidCostLastSavedAt,
} from '../utils/kidCostFormUtils';
import { pickLatestKidFigure } from '../utils/kidFigureUtils';
import type { KidFiguresRecordModel } from '../../../../../api/kids/types';
import { resolveKidFiguresStatus } from './KidFiguresOverview';

export interface CreateKidDocumentValues {
  // Context
  fundId: string;
  fundName: string;
  // Step 1 — Costs and Figures
  documentType: 'priips_kid' | 'ucits_kid';
  transactionCost: string;
  ongoingCharges: string;
  entryFee: string;
  exitFee: string;
  appliedPerformanceFee: string;
  notionalPerformanceFee: string;
  has_performance_fee: boolean;
  language: string;
  jurisdiction: string;

  // Step 2 — Method
  documentSourceType?: 'new' | 'duplicate';
  creationMethod?: 'blank' | 'duplicate' | 'recommended';
  template_share_class_id?: string;
  template_share_class_name?: string; // For UI display
  template_fund_name?: string; // For UI display in Review
  template_fund_id?: string; // For duplicate flow: fund ID of the source share class
  initialLayout?: string;
  /** Only set when documentSourceType is 'new' and creationMethod is 'recommended'. Undefined for blank or duplicate. */
  layoutStyle?: string;
  /** Only set when documentSourceType is 'new' and creationMethod is 'recommended'. For UI display only (layout name). */
  layoutStyleName?: string;
  /** Timestamp for the selected layout version. */
  layoutLastUpdatedTimestamp?: string;
  contentSelection?: string;
  /** Elements for the currently selected layout style (for preview + review regeneration) */
  layoutElements?: any[];
  /** Base64 PDF from Document Layout step (blank or recommended) for Review to reuse */
  layoutStepGeneratedPdfBase64?: string;

  // Step 3 — Template selection
  selectedTemplateIds: string[];

  // Step 4 — Details/Associations
  templateTitle: string;
}

interface CreateKidDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareClassId: string;
  shareClassName: string;
  fundId: string;
  fundName: string;
  jurisdiction: string;
  language: string;
}

const createKidDocumentStepLabels = [
  'Costs and Figures',
  'Document Layout',
  'Review',
];

const CreateKidDocumentModal: FunctionComponent<
  CreateKidDocumentModalProps
> = ({ isOpen, onClose, shareClassId, shareClassName, fundId, fundName, jurisdiction, language }) => {
  const { user_client_id: clientId } = TokenService.getUser()!;
  const getDefaultValues = (): CreateKidDocumentValues => ({
    fundId,
    fundName,
    documentType: 'priips_kid',
    transactionCost: '',
    ongoingCharges: '',
    entryFee: '',
    exitFee: '',
    appliedPerformanceFee: '0',
    notionalPerformanceFee: '0',
    has_performance_fee: false,
    language,
    jurisdiction,
    documentSourceType: undefined,
    creationMethod: undefined,
    layoutStyle: undefined,
    layoutStyleName: undefined,
    layoutLastUpdatedTimestamp: undefined,
    selectedTemplateIds: [],
    layoutElements: [],
    templateTitle: '',
  });

  const methods = useForm<CreateKidDocumentValues>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  });

  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasCostsInDb, setHasCostsInDb] = useState(false);
  const [costsLastSavedAt, setCostsLastSavedAt] = useState<string | null>(null);
  const [hasFiguresInDb, setHasFiguresInDb] = useState(false);
  const [latestFiguresRecord, setLatestFiguresRecord] =
    useState<KidFiguresRecordModel | null>(null);
  const [figuresRequireRecalculation, setFiguresRequireRecalculation] =
    useState(false);

  // Watch form values to reactively update Next button state
  const documentSourceType = methods.watch('documentSourceType');
  const creationMethod = methods.watch('creationMethod');
  const template_share_class_id = methods.watch('template_share_class_id');
  const layoutStyle = methods.watch('layoutStyle');
  const createKidMutation = useCreateKidDocumentMutation({
    clientId: clientId,
    fundId,
    shareClassId,
  });

  // Fetch existing cost variables (can be null when share class has no figures)
  const {
    data: existingCostVariables,
    isLoading: isLoadingCostVariables,
    isFetched: isCostVariablesFetched,
  } = useGetKidCostVariables(clientId, fundId, shareClassId, {
    enabled: isOpen,
  });
  const {
    data: latestKidFigures,
    isLoading: isLoadingLatestKidFigures,
    isFetched: isLatestKidFiguresFetched,
  } = useGetLatestKidFigures(clientId, fundId, shareClassId, {
    enabled: isOpen,
  });

  const createCostVariablesMutation = useCreateKidCosts();
  const calculateFiguresMutation = useCalculateKidFigures();

  // Prefill when we have figures for this share class; clear cost fields when fetch returns null
  useEffect(() => {
    if (!isOpen || !isCostVariablesFetched) return;

    if (existingCostVariables != null) {
      const currentValues = methods.getValues();
      const hasPerformanceFee =
        existingCostVariables.has_performance_fee ?? false;
      methods.reset({
        ...currentValues,
        transactionCost: toDisplayCost(existingCostVariables.transaction_cost),
        ongoingCharges: toDisplayCost(existingCostVariables.ongoing_cost),
        entryFee: toDisplayCost(existingCostVariables.entry_cost),
        exitFee: toDisplayCost(existingCostVariables.exit_cost),
        appliedPerformanceFee: hasPerformanceFee
          ? toDisplayCost(existingCostVariables.applied_performance_fee)
          : '0',
        notionalPerformanceFee: hasPerformanceFee
          ? toDisplayCost(existingCostVariables.notional_performance_fee)
          : '0',
        has_performance_fee: hasPerformanceFee,
      });
      setHasCostsInDb(true);
      setCostsLastSavedAt(getKidCostLastSavedAt(existingCostVariables));
    } else {
      // No costs for this share class: reset cost fields to defaults so we don't show stale data
      const currentValues = methods.getValues();
      methods.reset({
        ...currentValues,
        transactionCost: '',
        ongoingCharges: '',
        entryFee: '',
        exitFee: '',
        appliedPerformanceFee: '0',
        notionalPerformanceFee: '0',
        has_performance_fee: false,
      });
      setHasCostsInDb(false);
      setCostsLastSavedAt(null);
    }
  }, [
    isOpen,
    isCostVariablesFetched,
    existingCostVariables,
    methods,
  ]);


  useEffect(() => {
    if (!isOpen || !isLatestKidFiguresFetched) return;
    const latestFigure = pickLatestKidFigure(latestKidFigures);
    setHasFiguresInDb(Boolean(latestFigure));
    setLatestFiguresRecord(latestFigure);
    setFiguresRequireRecalculation(false);
  }, [isOpen, isLatestKidFiguresFetched, latestKidFigures]);


  useEffect(() => {
    if (isOpen) {
      createKidMutation.reset();
    }
  }, [isOpen]);


  useEffect(() => {
    if (!isOpen) {
      resetAll();
    }
  }, [isOpen]);

  // Clear selections when switching document source type
  useEffect(() => {
    if (documentSourceType === 'new') {
      // Clear duplicate selections when switching to new
      methods.setValue('template_share_class_id', undefined, { shouldDirty: false });
      methods.setValue('template_share_class_name', undefined, { shouldDirty: false });
      methods.setValue('template_fund_name', undefined, { shouldDirty: false });
      methods.setValue('template_fund_id', undefined, { shouldDirty: false });
      // Template style only exists when creationMethod is 'recommended'; clear when blank
      if (creationMethod !== 'recommended') {
        methods.setValue('layoutStyle', undefined, { shouldDirty: false });
        methods.setValue('layoutStyleName', undefined, { shouldDirty: false });
      }
    } else if (documentSourceType === 'duplicate') {
      // Clear template style when switching to duplicate
      methods.setValue('creationMethod', 'duplicate', { shouldDirty: true });
      methods.setValue('layoutStyle', undefined, { shouldDirty: false });
      methods.setValue('layoutStyleName', undefined, { shouldDirty: false });
    } else {
      // No source selected: clear dependent choices
      methods.setValue('creationMethod', undefined, { shouldDirty: false });
      methods.setValue('layoutStyle', undefined, { shouldDirty: false });
      methods.setValue('layoutStyleName', undefined, { shouldDirty: false });
      methods.setValue('template_share_class_id', undefined, { shouldDirty: false });
      methods.setValue('template_share_class_name', undefined, { shouldDirty: false });
      methods.setValue('template_fund_name', undefined, { shouldDirty: false });
      methods.setValue('template_fund_id', undefined, { shouldDirty: false });
    }
  }, [documentSourceType, creationMethod, methods.setValue]);


  // Check if current step can proceed (reactive based on watched values)
  const figuresStatus = resolveKidFiguresStatus(
    latestFiguresRecord,
    figuresRequireRecalculation,
    hasFiguresInDb,
  );

  const isCurrentStepValid = (): boolean => {
    switch (stepIndex) {
      case 0: // Costs and Figures
        return hasCostsInDb && figuresStatus === 'current';

      case 1: // Document Layout
        if (documentSourceType === 'duplicate') {
          // Duplicate requires template_share_class_id to be selected
          return !!template_share_class_id;
        } else if (documentSourceType === 'new') {
          if (creationMethod === 'blank') {
            // Blank is always valid
            return true;
          } else if (creationMethod === 'recommended') {
            // Recommended requires layoutStyle to be selected
            return !!layoutStyle;
          }
        }
        return false;

      default:
        return true;
    }
  };

  const goNext = async () => {
    // Check if current step is valid before proceeding
    if (!isCurrentStepValid()) {
      return;
    }
    const valid = await methods.trigger();
    if (!valid) return;
    setDirection(1);
    setStepIndex((s) =>
      Math.min(s + 1, createKidDocumentStepLabels.length - 1),
    );
  };

  const handleCreateCostVariables = async () => {
    const values = methods.getValues();
    const fieldsToValidate: (keyof CreateKidDocumentValues)[] = [
      'transactionCost',
      'ongoingCharges',
      'entryFee',
      'exitFee',
    ];
    if (values.has_performance_fee) {
      fieldsToValidate.push('appliedPerformanceFee', 'notionalPerformanceFee');
    }
    const valid = await methods.trigger(fieldsToValidate);
    if (!valid) return;

    try {
      const saved = await createCostVariablesMutation.mutateAsync({
        client_id: clientId,
        fund_id: values.fundId,
        share_class_id: shareClassId,
        transaction_cost: toBackendCost(values.transactionCost),
        ongoing_cost: toBackendCost(values.ongoingCharges),
        entry_cost: toBackendCost(values.entryFee),
        exit_cost: toBackendCost(values.exitFee),
        applied_performance_fee: values.has_performance_fee
          ? toBackendCost(values.appliedPerformanceFee)
          : 0,
        notional_performance_fee: values.has_performance_fee
          ? toBackendCost(values.notionalPerformanceFee)
          : 0,
        has_performance_fee: values.has_performance_fee,
      });
      setHasCostsInDb(true);
      setCostsLastSavedAt(getKidCostLastSavedAt(saved));
      // Costs changed, figures must be recalculated.
      setFiguresRequireRecalculation(true);
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  };

  const handleCalculateFigures = async () => {
    const values = methods.getValues();
    if (!hasCostsInDb) return;

    try {
      const calculatedFigures = await calculateFiguresMutation.mutateAsync({
        client_id: clientId,
        fund_id: values.fundId,
        share_class_id: shareClassId,
      });
      setHasFiguresInDb(true);
      setLatestFiguresRecord(calculatedFigures);
      setFiguresRequireRecalculation(false);
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  };

  const goPrev = () => {
    setDirection(-1);
    setStepIndex((s) => Math.max(s - 1, 0));
  };

  const resetAll = () => {
    methods.reset(getDefaultValues());
    setStepIndex(0);
    setDirection(1);
    setIsTransitioning(false);
    setHasCostsInDb(false);
    setCostsLastSavedAt(null);
    setHasFiguresInDb(false);
    setLatestFiguresRecord(null);
    setFiguresRequireRecalculation(false);
    calculateFiguresMutation.reset();
  };

  const onSubmit = async (values: CreateKidDocumentValues) => {
    await createKidMutation.mutateAsync({
      clientId: clientId,
      fundId: values.fundId,
      fundName: values.fundName,
      shareClassId,
      documentType: values.documentType,
      language: values.language,
      transactionCost: toBackendCost(values.transactionCost),
      ongoingCharges: toBackendCost(values.ongoingCharges),
      entryFee: toBackendCost(values.entryFee),
      exitFee: toBackendCost(values.exitFee),
      template_fund_id: values.template_fund_id,
      template_share_class_id: values.template_share_class_id,
      template_share_class_name: values.template_share_class_name,
      layout_style_id: values.layoutStyle,
      layout_last_updated_timestamp: values.layoutLastUpdatedTimestamp,
    });
    await new Promise((r) => setTimeout(r, 800));
    resetAll();
    onClose();
  };

  const renderStep = () => {
    switch (stepIndex) {
      case 0:
        return (
          <StepCostsAndFigures
            hasCostsInDb={hasCostsInDb}
            costsLastSavedAt={costsLastSavedAt}
            hasFiguresInDb={hasFiguresInDb}
            latestFiguresRecord={latestFiguresRecord}
            figuresRequireRecalculation={figuresRequireRecalculation}
            clientId={clientId}
            fundId={fundId}
            shareClassId={shareClassId}
            onCreateCostVariables={handleCreateCostVariables}
            onCalculateFigures={handleCalculateFigures}
            isCreating={createCostVariablesMutation.isPending}
            isCalculatingFigures={calculateFiguresMutation.isPending}
            isLoadingCosts={isLoadingCostVariables}
            isLoadingFigures={isLoadingLatestKidFigures}
            figuresCalculationError={
              calculateFiguresMutation.isError
                ? getUploadErrorMessage(
                    calculateFiguresMutation.error,
                    'Failed to calculate figures',
                  )
                : null
            }
          />
        );
      case 1:
        return <StepDocumentType shareClassId={shareClassId} shareClassName={shareClassName} />;
      case 2:
        return (
          <StepReview
            shareClassId={shareClassId}
            shareClassName={shareClassName}
          />
        );
      default:
        return null;
    }
  };

  const title = (
    <div className="items-center gap-2">
      <h2 className="text-lg font-bold underline underline-offset-2">
        Create New KID Document
      </h2>
      <span className="text-sm">
        {fundName} ({shareClassName})
      </span>
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={() => {
        resetAll();
        onClose();
      }}
      title={title}
      className="h-[75dvh] max-w-fit"
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex h-full flex-col"
        >
          <CreateKidDocumentStepper
            currentStep={stepIndex}
            steps={createKidDocumentStepLabels}
          />
          <div
            className={`scrollbar-custom grid min-h-0 flex-1 ${(isTransitioning || stepIndex === 1 || stepIndex === 2) ? 'overflow-hidden' : 'overflow-y-auto'
              }`}
          >
            <AnimatePresence mode="wait" initial={false} custom={direction}>
              <motion.div
                key={stepIndex}
                custom={direction}
                initial="incoming"
                animate="active"
                exit="exit"
                variants={formSliderVariants}
                transition={{
                  duration: 0.35,
                  ease: [0.56, 0.03, 0.12, 1.04],
                }}
                onAnimationStart={() => setIsTransitioning(true)}
                onAnimationComplete={() => setIsTransitioning(false)}
                className="col-start-1 row-start-1 min-h-0 h-full"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              onClick={() => {
                resetAll();
                onClose();
              }}
            >
              Cancel
            </button>

            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                disabled={stepIndex === 0}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Back
              </button>
              {stepIndex < createKidDocumentStepLabels.length - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!isCurrentStepValid()}
                  className="flex items-center justify-center gap-1.5 rounded-lg border bg-mainblue p-2 px-4 text-white transition-colors hover:bg-mainblue/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
                >
                  Next
                </button>
              ) : (
                <LoadingButton
                  type="submit"
                  disabled={isTransitioning}
                  className="flex items-center justify-center gap-1.5 rounded-lg border p-2 px-4 text-white transition-colors hover:bg-mainblue/90"
                  animate={Boolean(createKidMutation.error)}
                  isLoading={createKidMutation.isPending}
                  isSuccess={createKidMutation.isSuccess}
                  isError={Boolean(createKidMutation.error)}
                >
                  Create Document
                </LoadingButton>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </GenericModal>
  );
};

export default CreateKidDocumentModal;

// Note: step content components (StepCostsAndFigures, StepDocumentType, StepReview)
// have been moved into the ./form directory as their own components.
